'use client';

import { useEffect, useState } from 'react';

import { Box, CloseButton, Text } from '@opendoor/bricks-next';

const GiftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M18.5 21H5.5V12H3.5V8H20.5V12H18.5V21Z" fill="url(#paint0_linear_4031_637)" />
    <path
      d="M12 8V21M12 8C11.6383 6.5094 11.0154 5.23501 10.2127 4.34311C9.41003 3.45121 8.46469 2.98314 7.5 2.99995C6.83696 2.99995 6.20107 3.26334 5.73223 3.73218C5.26339 4.20102 5 4.83691 5 5.49995C5 6.16299 5.26339 6.79887 5.73223 7.26772C6.20107 7.73656 6.83696 7.99995 7.5 7.99995M12 8C12.3617 6.5094 12.9846 5.23501 13.7873 4.34311C14.59 3.45121 15.5353 2.98314 16.5 2.99995C17.163 2.99995 17.7989 3.26334 18.2678 3.73218C18.7366 4.20102 19 4.83691 19 5.49995C19 6.16299 18.7366 6.79887 18.2678 7.26772C17.7989 7.73656 17.163 7.99995 16.5 7.99995M19 12V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V12M3 9C3 8.73478 3.10536 8.48043 3.29289 8.29289C3.48043 8.10536 3.73478 8 4 8H20C20.2652 8 20.5196 8.10536 20.7071 8.29289C20.8946 8.48043 21 8.73478 21 9V11C21 11.2652 20.8946 11.5196 20.7071 11.7071C20.5196 11.8946 20.2652 12 20 12H4C3.73478 12 3.48043 11.8946 3.29289 11.7071C3.10536 11.5196 3 11.2652 3 11V9Z"
      stroke="#F9C940"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_4031_637"
        x1="16"
        y1="9"
        x2="12"
        y2="21"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#A51C1C" />
        <stop offset="1" stopColor="#E12020" />
      </linearGradient>
    </defs>
  </svg>
);

const BANNER_DISMISSED_KEY = 'inspection-banner-dismissed';

/**
 * Promotional banner for free home inspection report offer.
 * Part of experiment: growth-25-12-inspection-banner
 *
 * Impression tracking is handled at the Cloudflare Worker level.
 *
 * Figma: https://www.figma.com/design/PRkjHWTpRlrz6YFlekrChg/?node-id=4002-2104
 */
const InspectionBanner = () => {
  const [isDismissed, setIsDismissed] = useState(true); // Start hidden to prevent flash

  useEffect(() => {
    // Check localStorage on mount
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
    setIsDismissed(dismissed);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Box
      id="inspection-banner"
      display="flex"
      flexDirection="row"
      alignItems="center"
      paddingVertical="$3x"
      paddingHorizontal="$4x"
      gap="$3x"
      // Figma: #00278b (sem/color/background/state/hover/accent-brand)
      style={{ backgroundColor: '#00278b' }}
    >
      {/* Icon + Text container - flex={1} to fill space, left-aligned on mobile, centered on larger screens */}
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        $largerThanSM={{ justifyContent: 'center' }}
        gap="$2x"
        flex={1}
      >
        {/* Gift Icon */}
        <Box flexShrink={0} display="flex">
          <GiftIcon />
        </Box>

        {/* Text content - left-aligned on mobile, centered on larger screens */}
        <Text
          tag="p"
          typography="$bodyMedium"
          color="white"
          textAlign="left"
          $largerThanSM={{ textAlign: 'center' }}
        >
          Get a{' '}
          <Text tag="span" typography="$bodyMedium" fontWeight="$medium" color="white">
            FREE home inspection report
          </Text>{' '}
          ($450 value) with your Opendoor offer
        </Text>
      </Box>

      {/* Close Button - stays at the right */}
      <Box flexShrink={0}>
        <CloseButton
          analyticsName="cosmos-inspection-banner-close"
          onPress={handleDismiss}
          size="small"
          variant="inverse"
        />
      </Box>
    </Box>
  );
};

export default InspectionBanner;
